/**
 * useFinanceCounterparties hooks — Issue #369.
 *
 * 通过 @jinzhuan/api-client 调用对方单位 / 收款账户主数据接口；禁止前端伪状态。
 * 列表 / 详情默认脱敏；reveal 显式调用 reveal 接口，并由调用方在 UI 二次确认。
 */
import { useCallback, useState } from 'react';
import {
  createApiClient,
  ApiError as ApiClientError,
} from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export type FinanceCounterpartySummaryDto =
  ApiComponents['schemas']['FinanceCounterpartySummaryDto'];
export type FinanceCounterpartyDetailDto =
  ApiComponents['schemas']['FinanceCounterpartyDetailDto'];
export type FinanceCounterpartyListResponseDto =
  ApiComponents['schemas']['FinanceCounterpartyListResponseDto'];
export type CreateCounterpartyDto =
  ApiComponents['schemas']['CreateCounterpartyDto'];
export type UpdateCounterpartyDto =
  ApiComponents['schemas']['UpdateCounterpartyDto'];
export type CounterpartyTransitionDto =
  ApiComponents['schemas']['CounterpartyTransitionDto'];
export type RevealRequestDto =
  ApiComponents['schemas']['RevealRequestDto'];
export type RevealResponseDto =
  ApiComponents['schemas']['RevealResponseDto'];
export type FinancePayeeAccountSummaryDto =
  ApiComponents['schemas']['FinancePayeeAccountSummaryDto'];
export type FinancePayeeAccountDetailDto =
  ApiComponents['schemas']['FinancePayeeAccountDetailDto'];
export type FinancePayeeAccountListResponseDto =
  ApiComponents['schemas']['FinancePayeeAccountListResponseDto'];
export type CreatePayeeAccountDto =
  ApiComponents['schemas']['CreatePayeeAccountDto'];
export type UpdatePayeeAccountDto =
  ApiComponents['schemas']['UpdatePayeeAccountDto'];
export type VerifyPayeeAccountDto =
  ApiComponents['schemas']['VerifyPayeeAccountDto'];
export type PayeeAccountTransitionDto =
  ApiComponents['schemas']['PayeeAccountTransitionDto'];
export type SetDefaultPayeeAccountDto =
  ApiComponents['schemas']['SetDefaultPayeeAccountDto'];
export type PayeeSnapshotRequestDto =
  ApiComponents['schemas']['PayeeSnapshotRequestDto'];
export type PayeeSnapshotResponseDto =
  ApiComponents['schemas']['PayeeSnapshotResponseDto'];

export interface ApiError {
  message: string;
  code?: string;
}

function toApiError(err: unknown, status?: number): ApiError {
  const effective =
    status ?? (err instanceof ApiClientError ? err.status : undefined);
  if (effective === 401) {
    return { message: '会话已失效，请重新登录后再试', code: 'UNAUTHENTICATED' };
  }
  if (effective === 403) {
    return { message: '没有权限执行该操作', code: 'FORBIDDEN' };
  }
  if (effective === 404) {
    return { message: '记录不存在或已被删除', code: 'NOT_FOUND' };
  }
  if (effective === 409) {
    return { message: '数据已被其他用户修改，请刷新后重试', code: 'CONFLICT' };
  }
  if (err instanceof Error) return { message: err.message };
  return { message: String(err) };
}

// ─── Counterparties ──────────────────────────────────────────────────────────

export interface ListCounterpartiesFilter {
  status?: string;
  counterpartyType?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export function useFinanceCounterparties() {
  const [data, setData] =
    useState<FinanceCounterpartyListResponseDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchList = useCallback(async (filter?: ListCounterpartiesFilter) => {
    setLoading(true);
    setError(null);
    try {
      const query: Record<string, string | number> = {};
      if (filter?.status) query.status = filter.status;
      if (filter?.counterpartyType)
        query.counterpartyType = filter.counterpartyType;
      if (filter?.search) query.search = filter.search;
      if (filter?.page) query.page = filter.page;
      if (filter?.limit) query.limit = filter.limit;
      const { data: ok, error: apiErr, response } = await apiClient.GET(
        '/api/v1/finance/counterparties',
        { params: { query } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      setData(ok ?? null);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchList };
}

export function useFinanceCounterpartyDetail() {
  const [data, setData] =
    useState<FinanceCounterpartyDetailDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchDetail = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data: ok, error: apiErr, response } = await apiClient.GET(
        '/api/v1/finance/counterparties/{id}',
        { params: { path: { id } } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        setData(null);
        return;
      }
      setData(ok ?? null);
    } catch (e) {
      setError(toApiError(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const clear = useCallback(() => setData(null), []);

  return { data, loading, error, fetchDetail, clear };
}

export function useFinanceCounterpartyActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const create = useCallback(async (dto: CreateCounterpartyDto) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/finance/counterparties',
        { body: dto },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const update = useCallback(
    async (id: string, dto: UpdateCounterpartyDto) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.PUT(
          '/api/v1/finance/counterparties/{id}',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const suspend = useCallback(
    async (id: string, dto: CounterpartyTransitionDto) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/counterparties/{id}/suspend',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const reactivate = useCallback(
    async (id: string, dto: CounterpartyTransitionDto) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/counterparties/{id}/reactivate',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const archive = useCallback(
    async (id: string, dto: CounterpartyTransitionDto) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/counterparties/{id}/archive',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const reveal = useCallback(async (id: string, dto: RevealRequestDto) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/finance/counterparties/{id}/reveal',
        { params: { path: { id } }, body: dto },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { loading, error, create, update, suspend, reactivate, archive, reveal };
}

// ─── Payee accounts ─────────────────────────────────────────────────────────

export interface ListPayeeAccountsFilter {
  counterpartyId?: string;
  status?: string;
  verificationStatus?: string;
  isDefault?: boolean;
  page?: number;
  limit?: number;
}

export function useFinancePayeeAccounts() {
  const [data, setData] =
    useState<FinancePayeeAccountListResponseDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchList = useCallback(async (filter?: ListPayeeAccountsFilter) => {
    setLoading(true);
    setError(null);
    try {
      const query: Record<string, string | number | boolean> = {};
      if (filter?.counterpartyId) query.counterpartyId = filter.counterpartyId;
      if (filter?.status) query.status = filter.status;
      if (filter?.verificationStatus)
        query.verificationStatus = filter.verificationStatus;
      if (typeof filter?.isDefault === 'boolean')
        query.isDefault = filter.isDefault;
      if (filter?.page) query.page = filter.page;
      if (filter?.limit) query.limit = filter.limit;
      const { data: ok, error: apiErr, response } = await apiClient.GET(
        '/api/v1/finance/payee-accounts',
        { params: { query } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      setData(ok ?? null);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchList };
}

export function useFinancePayeeAccountActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const create = useCallback(async (dto: CreatePayeeAccountDto) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/finance/payee-accounts',
        { body: dto },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const verify = useCallback(
    async (id: string, dto: VerifyPayeeAccountDto) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/payee-accounts/{id}/verify',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const setDefault = useCallback(
    async (id: string, dto: SetDefaultPayeeAccountDto) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/payee-accounts/{id}/set-default',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const reveal = useCallback(async (id: string, dto: RevealRequestDto) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/finance/payee-accounts/{id}/reveal',
        { params: { path: { id } }, body: dto },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const snapshot = useCallback(async (dto: PayeeSnapshotRequestDto) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/finance/payee-accounts/snapshot',
        { body: dto },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { loading, error, create, verify, setDefault, reveal, snapshot };
}
